import React from 'react';
import Icon from '../../../components/AppIcon';

const AIRecommendationsPanel = ({ financialData, loading = false }) => {
  const recommendations = [
    {
      id: 1,
      title: 'Reduce Dining Expenses',
      description: "You\'ve spent 93% of your dining budget. Consider meal prepping to save $120/month.",
      icon: 'Utensils',
      priority: 'high',
      impact: '$120/month',
      timeframe: 'Immediate',
      color: 'var(--color-error)'
    },
    {
      id: 2,
      title: 'Optimize Subscription Services',
      description: 'You have 8 active subscriptions totaling $156/month. Cancel unused services to save money.',
      icon: 'CreditCard',
      priority: 'medium',
      impact: '$78/month',
      timeframe: '1-2 weeks',
      color: 'var(--color-warning)'
    },
    {
      id: 3,
      title: 'Increase Emergency Fund',
      description: 'Your emergency fund covers 4.2 months. Aim for 6 months to improve financial security.',
      icon: 'Shield',
      priority: 'low',
      impact: '$2,400 total',
      timeframe: '6-12 months',
      color: 'var(--color-success)'
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-700 border-green-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl p-4 md:p-6 shadow-elevation-2">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-48 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
            <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-64 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
          </div>
        </div>
        <div className="space-y-4">
          {[...Array(3)]?.map((_, i) => (
            <div key={i} className="p-4 rounded-xl bg-muted animate-pulse">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-200 to-gray-300 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-3/4 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                  <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-full animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                  <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-5/6 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl p-4 md:p-6 shadow-elevation-2 hover:shadow-elevation-3 transition-all duration-300">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-fintech-purple-500 to-fintech-blue-500 flex items-center justify-center shadow-elevation-2 animate-pulse-slow">
          <Icon name="Sparkles" size={20} color="#FFFFFF" />
        </div>
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-foreground mb-1">AI Recommendations</h2>
          <p className="text-sm text-muted-foreground">Personalized insights to improve your finances</p>
        </div>
      </div>
      <div className="space-y-4">
        {recommendations?.map((rec, index) => (
          <div
            key={rec?.id}
            className="p-4 rounded-xl bg-muted hover:bg-muted/80 transition-all duration-300 hover:shadow-elevation-2 hover:scale-[1.01] cursor-pointer group animate-fade-in"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3" style={{ backgroundColor: `${rec?.color}20` }}>
                <Icon name={rec?.icon} size={20} color={rec?.color} />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-foreground transition-colors duration-200 group-hover:text-primary">{rec?.title}</h3>
                  <span className={`px-2 py-1 rounded-lg text-xs font-medium border transition-all duration-200 hover:scale-110 ${getPriorityColor(rec?.priority)}`}>
                    {rec?.priority?.toUpperCase()}
                  </span>
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mb-3">{rec?.description}</p>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1 px-2 py-1 bg-success/10 rounded-lg transition-all duration-200 hover:scale-105">
                    <Icon name="DollarSign" size={14} className="text-success" />
                    <span className="text-xs font-medium text-success">{rec?.impact}</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 rounded-lg transition-all duration-200 hover:scale-105">
                    <Icon name="Clock" size={14} className="text-primary" />
                    <span className="text-xs font-medium text-primary">{rec?.timeframe}</span>
                  </div>
                </div>
              </div>
            </div>
            <button className="w-full mt-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-sm font-medium transition-all duration-200 hover:scale-[1.02] flex items-center justify-center gap-2">
              <Icon name="CheckCircle" size={16} />
              Apply Recommendation
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AIRecommendationsPanel;
